import { useState } from 'react';
import {
  Anchor,
  Badge,
  Button,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '@/api/admin';
import { errorMessage } from '@/api/http';
import { qk } from '@/api/queries';
import type { AdminProgramme } from '@/api/types';
import { CollectionSelect, CreatorSelect } from '@/components/pickers';
import { useProgrammeFormContext } from './formContext';

const SHORT_MAX = 140;

const AGE_RATINGS = [
  { value: '0', label: 'U — suitable for all' },
  { value: '4', label: '4+' },
  { value: '6', label: '6+' },
  { value: '8', label: '8+' },
  { value: '10', label: '10+' },
  { value: '12', label: '12+' },
];

export function DetailsTab({ programme }: { programme: AdminProgramme }) {
  const form = useProgrammeFormContext();
  const short = form.getValues().synopsis_short;
  const ageRating = form.getValues().age_rating;
  const rating = ageRating == null ? null : String(ageRating);
  const ratingData =
    rating == null || AGE_RATINGS.some((r) => r.value === rating)
      ? AGE_RATINGS
      : [...AGE_RATINGS, { value: rating, label: `${rating}+` }];

  return (
    <Stack gap="lg">
      <Section title="Basics">
        <TextInput label="Title" required {...form.getInputProps('title')} />
        <TextInput
          label="Slug"
          description="Used in links and preview URLs. Lowercase letters, numbers and hyphens."
          {...form.getInputProps('slug')}
          onChange={(e) =>
            form.setFieldValue(
              'slug',
              e.currentTarget.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
            )
          }
        />
        <Textarea
          label="Short synopsis"
          description="One line for cards and search results. Plain and warm; written for grown-ups reading over a child’s shoulder."
          required
          autosize
          minRows={2}
          maxLength={SHORT_MAX}
          {...form.getInputProps('synopsis_short')}
          rightSection={
            <Text size="xs" c={short.length > SHORT_MAX - 10 ? 'orange.8' : 'dimmed'} pr="xs">
              {short.length}/{SHORT_MAX}
            </Text>
          }
          rightSectionWidth={64}
        />
        <Textarea
          label="Full synopsis"
          description="Shown on the programme page."
          autosize
          minRows={4}
          {...form.getInputProps('synopsis')}
        />
        <CreatorSelect
          label="Creator or content partner"
          required
          clearable
          nothingFound="No creator with that name — add one under Creators."
          selected={programme.creator}
          value={form.getValues().creator_id ?? null}
          onChange={(v) => form.setFieldValue('creator_id', v)}
          error={form.errors.creator_id}
        />
      </Section>

      <Section title="Age suitability">
        <SimpleGrid cols={{ base: 1, xs: 3 }}>
          <NumberInput
            label="Minimum age"
            min={0}
            max={18}
            required
            {...form.getInputProps('min_age')}
          />
          <NumberInput
            label="Maximum age"
            min={0}
            max={18}
            required
            {...form.getInputProps('max_age')}
          />
          <Select
            label="Age rating"
            description="Compared with each child’s safety settings."
            data={ratingData}
            placeholder="Choose a rating"
            required
            value={rating}
            onChange={(v) => form.setFieldValue('age_rating', v == null ? null : Number(v))}
            error={form.errors.age_rating}
          />
        </SimpleGrid>
      </Section>

      <Section title="Search and discovery">
        <TagsInput
          label="Search tags"
          description="Extra words people might search for (press Enter after each)."
          clearable
          splitChars={[',']}
          {...form.getInputProps('search_tags')}
        />
        <SimpleGrid cols={{ base: 1, xs: 2 }}>
          <NumberInput
            label="Editorial priority"
            description="Higher wins ties in rows. 0 is normal."
            min={-100}
            max={100}
            {...form.getInputProps('editorial_priority')}
          />
          <Switch
            mt={{ xs: 'lg' }}
            label="Eligible for discovery"
            description="Can appear in “Surprise yourself” for children who haven’t shown this interest."
            {...form.getInputProps('discovery_eligible', { type: 'checkbox' })}
          />
        </SimpleGrid>
      </Section>

      <CollectionsSection programme={programme} />
    </Stack>
  );
}

export function Section({
  title,
  description,
  children,
  actions,
}: {
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      className="pb-surface"
      component="section"
      aria-label={title}
    >
      <Stack gap="sm">
        <Group justify="space-between" gap="xs">
          <div>
            <Title order={2} size="h3">
              {title}
            </Title>
            {description && (
              <Text size="sm" c="dimmed">
                {description}
              </Text>
            )}
          </div>
          {actions}
        </Group>
        {children}
      </Stack>
    </Paper>
  );
}

function CollectionsSection({ programme }: { programme: AdminProgramme }) {
  const qc = useQueryClient();
  const [choice, setChoice] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: async (collectionId: string) => {
      const c = await adminApi.getCollection(collectionId);
      const ids = (c.items ?? []).map((i) => i.programme_id);
      return adminApi.setCollectionItems(collectionId, [...ids, programme.id]);
    },
    onSuccess: (c) => {
      setChoice(null);
      void qc.invalidateQueries({ queryKey: qk.programme(programme.id) });
      void qc.invalidateQueries({ queryKey: ['collections'] });
      void qc.invalidateQueries({ queryKey: qk.collection(c.id) });
      notifications.show({ color: 'leaf', message: `Added to ${c.title}.` });
    },
    onError: (e) =>
      notifications.show({ color: 'coral', title: 'Not added', message: errorMessage(e) }),
  });
  const memberIds = new Set(programme.collections.map((c) => c.id));

  return (
    <Section
      title="Collections"
      description="Editorial rows and the hero this programme appears in. Order within a collection is set on the collection."
    >
      {programme.collections.length === 0 ? (
        <Text size="sm" c="dimmed">
          Not in any collection yet.
        </Text>
      ) : (
        <Group gap="xs">
          {programme.collections.map((c) => (
            <Badge
              key={c.id}
              variant="outline"
              color="blossom"
              size="lg"
              component={Link}
              to={`/collections/${c.id}`}
              style={{ cursor: 'pointer', textTransform: 'none' }}
            >
              {c.title}
            </Badge>
          ))}
        </Group>
      )}
      <Group gap="xs" align="flex-end">
        <CollectionSelect
          label="Add to collection"
          placeholder="Choose a collection"
          nothingFound="No matching collections"
          exclude={(c) => memberIds.has(c.id)}
          value={choice}
          onChange={setChoice}
          w={280}
        />
        <Button
          variant="default"
          leftSection={<IconPlus size={16} />}
          disabled={!choice}
          loading={add.isPending}
          onClick={() => choice && add.mutate(choice)}
        >
          Add
        </Button>
        <Anchor component={Link} to="/collections" size="sm">
          Manage collections
        </Anchor>
      </Group>
    </Section>
  );
}
